import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, Check, AlertTriangle, X, Repeat, Package, Play, ChevronRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getVenues, getVenueRooms, runRiderCheck, getMyStructuredRiders } from '../lib/api';

/* v1 shows one of four results per rider line and nothing else. The engine
   still returns readiness_pct, an action plan, coaching tips, action verbs and
   the swap-confirm loop — none of that is rendered here. */

// Order is the reading order of the groups: what's handled, then what isn't.
const RESULTS = ['covered', 'covered_equivalent', 'check_with_venue', 'missing'];

const STATUS_COLOR = {
  covered:            { solid: 'var(--green-ok, #5A9A6B)', icon: Check, word: 'Covered' },
  covered_equivalent: { solid: 'var(--green-ok, #5A9A6B)', icon: Repeat, word: 'Covered by Equivalent' },
  check_with_venue:   { solid: 'var(--s8-amber, #C8923E)', icon: AlertTriangle, word: 'Check with Venue' },
  missing:            { solid: 'var(--s8-red, #B85040)', icon: X, word: 'Missing' },
};

const GROUP_BLURB = {
  covered: 'The venue has exactly what you asked for.',
  covered_equivalent: 'Different model, same class — the map vouches for it.',
  check_with_venue: "The engine can't judge these. Ask the venue directly.",
  missing: "The venue doesn't have it, or not enough of it.",
};

const CARD_STYLE = {
  background: '#1E1E1A',
  border: '1px solid rgba(232, 226, 214, 0.14)',
  borderRadius: 12,
  padding: '24px 24px',
};

const MONO_LABEL = {
  fontFamily: "'DM Mono', monospace",
  fontSize: 11,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: '#a39889',
};

const PICKER_LABEL_STYLE = { ...MONO_LABEL, fontSize: 10.5, marginBottom: 6 };

function StatusIcon({ status, size = 16 }) {
  const cfg = STATUS_COLOR[status] || STATUS_COLOR.covered;
  const Icon = cfg.icon;
  return <Icon size={size} color={cfg.solid} strokeWidth={2.5} aria-hidden />;
}

/* ─── Pairing card: which rider × which room, no run metadata (that lives in
   the controls row above, tied to the Selects) ─── */
function PairingCard({ artist, venue, room, riderName }) {
  return (
    <section style={{ ...CARD_STYLE, marginTop: 24 }} data-testid="pairing-card">
      <div
        className="flex flex-col gap-4"
        style={{ ['--pairing-gap']: '18px' }}
      >
        <style>{`
          @media (min-width: 640px) {
            .rc-pairing-row { flex-direction: row; align-items: center; }
            .rc-pairing-entity { flex: 1 1 0; }
            .rc-pairing-x { flex: 0 0 auto; font-size: 18px !important; }
            .rc-pairing-x::before, .rc-pairing-x::after { display: none; }
          }
        `}</style>
        <div className="rc-pairing-row flex flex-col" style={{ gap: 16 }}>
          <div className="rc-pairing-entity" style={{ minWidth: 0 }}>
            <div style={{ ...MONO_LABEL, fontSize: 10.5 }}>Artist</div>
            <div style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontVariantNumeric: 'tabular-nums', fontSize: 22, letterSpacing: '-0.01em', lineHeight: 1.15, color: '#F0EBE0', marginTop: 3 }}>
              {artist.name}
            </div>
            {riderName && (
              <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12.5, color: '#a39889', marginTop: 2 }}>
                {riderName}
              </div>
            )}
          </div>
          <div
            className="rc-pairing-x"
            aria-hidden
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#a39889',
            }}
          >
            <span style={{ flex: '1 1 auto', height: 1, background: 'rgba(232,226,214,0.08)' }} />
            ×
            <span style={{ flex: '1 1 auto', height: 1, background: 'rgba(232,226,214,0.08)' }} />
          </div>
          <div className="rc-pairing-entity" style={{ minWidth: 0 }}>
            <div style={{ ...MONO_LABEL, fontSize: 10.5 }}>Venue</div>
            <div style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontVariantNumeric: 'tabular-nums', fontSize: 22, letterSpacing: '-0.01em', lineHeight: 1.15, color: '#F0EBE0', marginTop: 3 }}>
              {venue.name}
            </div>
            {room && (
              <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12.5, color: '#a39889', marginTop: 2 }}>
                {room.name}{room.capacity ? ` · ${room.capacity} cap` : ''}
              </div>
            )}
            {room?.inventory_confirmed_at && (
              <div
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 6,
                  fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: '0.05em', color: '#5A9A6B',
                }}
              >
                <Check size={11} strokeWidth={3} aria-hidden />
                equipment confirmed {new Date(room.inventory_confirmed_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Detail cards ─── */
function DetailCard({ line }) {
  const cfg = STATUS_COLOR[line.status] || STATUS_COLOR.covered;
  return (
    <div
      data-testid={`detail-card-${line.line_id}`}
      style={{
        background: '#1E1E1A',
        borderLeft: `3px solid ${cfg.solid}`,
        borderTop: '1px solid rgba(232, 226, 214, 0.08)',
        borderRight: '1px solid rgba(232, 226, 214, 0.08)',
        borderBottom: '1px solid rgba(232, 226, 214, 0.08)',
        borderRadius: 8,
        padding: '14px 16px',
        marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <StatusIcon status={line.status} size={17} />
        <div style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: 16, letterSpacing: '0.01em', color: '#F0EBE0' }}>
          {line.category_label}
        </div>
        <span
          style={{
            marginLeft: 'auto',
            fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase',
            fontWeight: 700, color: cfg.solid,
          }}
        >
          {cfg.word}
        </span>
      </div>
      <div style={{ marginTop: 10, fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#F0EBE0', lineHeight: 1.7 }}>
        <div>
          <span style={{ color: '#8E8474' }}>Rider: </span>
          {line.rider.quantity > 1 ? `${line.rider.quantity} × ` : ''}{line.rider.display}
        </div>
        <div>
          <span style={{ color: '#8E8474' }}>Venue: </span>
          {line.venue.display === 'none' || !line.venue.display
            ? 'none'
            : `${line.venue.quantity > 1 ? `${line.venue.quantity} × ` : ''}${line.venue.display}`}
        </div>
        {line.coverage && (
          <div style={{ marginTop: 4, color: '#C8923E' }} data-testid={`coverage-${line.line_id}`}>
            {line.coverage.comparable_on_hand} on hand · {line.coverage.shortfall} short
          </div>
        )}
      </div>
      <div style={{ marginTop: 11, fontFamily: "'Barlow', sans-serif", fontSize: 12.5, color: '#a39889' }}>
        {line.reason}
      </div>
    </div>
  );
}

/* ─── One group per result, with its own count ─── */
function ResultGroup({ status, lines }) {
  if (lines.length === 0) return null;
  const cfg = STATUS_COLOR[status];
  return (
    <section style={{ marginBottom: 26 }} data-testid={`group-${status}`}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
        <StatusIcon status={status} size={15} />
        <span style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: 18, letterSpacing: '0.02em', color: '#F0EBE0' }}>
          {cfg.word}
        </span>
        <span
          data-testid={`count-${status}`}
          style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 700, color: cfg.solid }}
        >
          {lines.length}
        </span>
      </div>
      <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12.5, color: '#8E8474', marginBottom: 12 }}>
        {GROUP_BLURB[status]}
      </p>
      {lines.map((line) => <DetailCard key={line.line_id} line={line} />)}
    </section>
  );
}

/* ─── Band-provided lines: outside the four results, never a result ─── */
function BandBringsGroup({ lines }) {
  if (!lines || lines.length === 0) return null;
  return (
    <section style={{ marginBottom: 26 }} data-testid="group-band-provided">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
        <Package size={15} color="#8E8474" strokeWidth={2.5} aria-hidden />
        <span style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500, fontSize: 18, letterSpacing: '0.02em', color: '#F0EBE0' }}>
          You Bring
        </span>
        <span
          data-testid="count-band-provided"
          style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 700, color: '#8E8474' }}
        >
          {lines.length}
        </span>
      </div>
      <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12.5, color: '#8E8474', marginBottom: 12 }}>
        Your own gear — not checked against the venue.
      </p>
      <div style={{ border: '1px dashed rgba(232, 226, 214, 0.14)', borderRadius: 8, padding: '10px 14px' }}>
        {lines.map((b) => (
          <div
            key={b.line_id}
            data-testid={`band-line-${b.line_id}`}
            style={{ display: 'flex', gap: 10, padding: '6px 0', fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#C4BAA8' }}
          >
            <span style={{ flex: '1 1 auto' }}>{b.category_label}</span>
            <span style={{ color: '#8E8474' }}>
              {b.rider.quantity > 1 ? `${b.rider.quantity} × ` : ''}{b.rider.display}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── About this check (right rail): status legend is always visible — it's
   what a first-time viewer actually needs; the narrative content collapses ─── */
function AboutThisCheck({ mapVersion }) {
  const [expanded, setExpanded] = useState(false);
  const body = { fontFamily: "'Barlow', sans-serif", fontSize: 13, color: '#d4c5a8', lineHeight: 1.6 };
  const dim = { ...body, color: '#a39889' };
  return (
    <section style={{ ...CARD_STYLE }} data-testid="about-this-check">
      <div style={MONO_LABEL}>Status legend</div>
      <div style={{ marginTop: 13, display: 'flex', flexDirection: 'column', gap: 13 }}>
        {[
          ['covered', 'Exact model in stock at the full quantity, or any model in a class you accept.'],
          ['covered_equivalent', 'A different model the map places in the same class. No confirmation needed.'],
          ['check_with_venue', 'The venue has gear in this category but the engine cannot judge it — an unmapped model, or one they have not named.'],
          ['missing', 'A confident no: nothing in the category, a different class, or not enough of them.'],
        ].map(([status, desc]) => (
          <div key={status} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ marginTop: 2 }}><StatusIcon status={status} size={16} /></span>
            <div>
              <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 13, color: '#F0EBE0', fontWeight: 600 }}>
                {STATUS_COLOR[status].word}
              </div>
              <div style={dim}>{desc}</div>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid="about-this-check-toggle"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%',
          background: 'transparent', border: 'none', borderTop: '1px solid rgba(232, 226, 214, 0.08)',
          marginTop: 18, padding: '16px 0 0', cursor: 'pointer', color: '#8E8474',
          fontFamily: "'Barlow', sans-serif", fontSize: 12.5, textAlign: 'left',
        }}
      >
        <span>How the check works, data sources</span>
        <ChevronRight
          size={15}
          style={{ transition: 'transform 200ms ease', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', flex: '0 0 auto' }}
          aria-hidden
        />
      </button>
      {expanded && (
        <div data-testid="about-this-check-body">
          <div style={{ marginTop: 18 }}>
            <div style={MONO_LABEL}>How it works</div>
            <p style={{ ...body, marginTop: 10 }}>
              Each rider line is compared against the venue&apos;s confirmed inventory: exact model first,
              then a curated equivalence class (e.g. an SM58 swapped for a Beta 58A). Quantities are
              validated against stock; console channels are compared as totals.
            </p>
          </div>
          <div style={{ marginTop: 24 }}>
            <div style={MONO_LABEL}>Conservative by design</div>
            <p style={{ ...dim, marginTop: 10 }}>
              Swaps the equivalence map isn&apos;t confident about are treated as <em>must be sourced</em> —
              a harsh verdict here is caution, not an error. Uncertainty costs a rental suggestion,
              never a false &quot;you&apos;re fine&quot;.
            </p>
          </div>
          <div style={{ marginTop: 24 }}>
            <div style={MONO_LABEL}>Data sources</div>
            <ul style={{ marginTop: 10, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <li style={dim}><span style={{ color: '#F0EBE0' }}>Venue inventory</span> — entered and confirmed by venue staff.</li>
              <li style={dim}><span style={{ color: '#F0EBE0' }}>Artist rider</span> — your structured rider lines (versioned).</li>
              <li style={dim}><span style={{ color: '#F0EBE0' }}>Equivalence map</span> — Stage8-curated model classes ({mapVersion || 'v0'}).</li>
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}

export default function RiderCheckRunPage() {
  const [searchParams] = useSearchParams();
  const [venues, setVenues] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [riders, setRiders] = useState([]);
  const [venueId, setVenueId] = useState(searchParams.get('venue') || '');
  const [roomId, setRoomId] = useState(searchParams.get('room') || '');
  const [riderVariantId, setRiderVariantId] = useState(searchParams.get('rider') || '');
  const [hasRider, setHasRider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [venuesRes, ridersRes] = await Promise.all([getVenues(), getMyStructuredRiders()]);
        setVenues(venuesRes.data || []);
        const mine = ridersRes.data?.riders || [];
        setRiders(mine);
        setHasRider(mine.some((r) => r.rider?.lines?.length));
        setRiderVariantId((current) =>
          current || mine.find((r) => r.rider?.lines?.length)?.rider?.variantId || '');
      } catch {
        setHasRider(false);
      }
    })();
  }, []);

  // Rooms belong to the chosen venue; picking a different venue invalidates the
  // room, so it resets rather than silently checking against another stage.
  useEffect(() => {
    if (!venueId) {
      setRooms([]);
      setRoomId('');
      return;
    }
    let stale = false;
    (async () => {
      try {
        const res = await getVenueRooms(venueId);
        if (stale) return;
        const list = res.data || [];
        setRooms(list);
        setRoomId((current) =>
          list.some((r) => r.id === current) ? current : (list[0]?.id || ''));
      } catch {
        if (!stale) { setRooms([]); setRoomId(''); }
      }
    })();
    return () => { stale = true; };
  }, [venueId]);

  const handleRun = async () => {
    if (!roomId || !riderVariantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await runRiderCheck(roomId, riderVariantId);
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Check failed');
    } finally {
      setLoading(false);
    }
  };

  const canRun = Boolean(roomId && riderVariantId) && hasRider !== false;

  // One bucket per result, in RESULTS order. Grouping replaces the old
  // severity sort — there is no ranking to show, only four answers.
  const byResult = RESULTS.reduce((acc, status) => {
    acc[status] = (result?.lines || []).filter((l) => l.status === status);
    return acc;
  }, {});

  return (
    <div className="content-container" data-testid="rider-check-run-page">
      <h1 className="page-title gold-accent">Rider Check</h1>
      <p className="text-sm text-muted-foreground mt-1">
        Your rider against a venue&apos;s confirmed equipment — what to rent, bring, or confirm before load-in.
      </p>

      {hasRider === false && (
        <div
          style={{ ...CARD_STYLE, marginTop: 24 }}
          data-testid="no-rider-banner"
        >
          <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 14, color: '#d4c5a8' }}>
            You don&apos;t have a structured rider yet.{' '}
            <Link to="/musicians/me/rider" style={{ color: '#C8923E' }}>Build your rider</Link>{' '}
            first — the check runs against it.
          </p>
        </div>
      )}

      {/* Controls: rider × venue × room → run */}
      <div className="mt-6 flex flex-wrap items-end gap-4">
        <div className="min-w-[220px]">
          <div style={PICKER_LABEL_STYLE}>Rider</div>
          <Select value={riderVariantId} onValueChange={setRiderVariantId}>
            <SelectTrigger className="h-11 bg-[#151515] border-white/[0.08] text-white" data-testid="rider-select">
              <SelectValue placeholder="Pick a rider" />
            </SelectTrigger>
            <SelectContent>
              {riders.map(({ rider }) => (
                <SelectItem key={rider.variantId} value={rider.variantId}>
                  {rider.name}
                  {rider.lines?.length ? ` — ${rider.lines.length} lines` : ' — empty'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[240px]">
          <div style={PICKER_LABEL_STYLE}>Venue</div>
          <Select value={venueId} onValueChange={setVenueId}>
            <SelectTrigger className="h-11 bg-[#151515] border-white/[0.08] text-white" data-testid="venue-select">
              <SelectValue placeholder="Pick a venue" />
            </SelectTrigger>
            <SelectContent>
              {venues.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {(v.venueName || v.displayName) + (v.city ? ` — ${v.city}` : '')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[200px]">
          <div style={PICKER_LABEL_STYLE}>Room</div>
          <Select value={roomId} onValueChange={setRoomId} disabled={!venueId || rooms.length === 0}>
            <SelectTrigger className="h-11 bg-[#151515] border-white/[0.08] text-white" data-testid="room-select">
              <SelectValue placeholder={venueId ? 'Pick a room' : 'Pick a venue first'} />
            </SelectTrigger>
            <SelectContent>
              {rooms.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name}{r.capacity ? ` — ${r.capacity} cap` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          onClick={handleRun}
          disabled={loading || !canRun}
          data-testid="run-check-btn"
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 13,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            padding: '12px 28px',
            background: '#C8923E',
            color: '#0E1110',
            borderRadius: 6,
            border: 'none',
            cursor: loading || !canRun ? 'not-allowed' : 'pointer',
            opacity: loading || !canRun ? 0.65 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            fontWeight: 600,
          }}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Play size={13} />}
          {loading ? 'Running…' : result ? 'Run again' : 'Run check'}
        </button>
        {result && (
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#a39889' }} data-testid="last-run">
            Last run: {new Date(result.generated_at).toLocaleString()}
            {result.cached ? ' (no changes since)' : ''} · rider v{result.rider_version}
          </span>
        )}
      </div>

      {error && (
        <div
          data-testid="error-block"
          style={{
            marginTop: 20, padding: '12px 16px', borderRadius: 8,
            background: 'rgba(212, 84, 58, 0.12)', border: '1px solid rgba(212, 84, 58, 0.4)',
            color: '#F0A08F', fontFamily: "'Barlow', sans-serif", fontSize: 13,
          }}
        >
          {typeof error === 'string' ? error : 'Check failed'}
        </div>
      )}

      {result && (
        <>
          <PairingCard
            artist={result.artist}
            venue={result.venue}
            room={result.room}
            riderName={result.rider_name}
          />

          {result.backline_empty ? (
            <div
              data-testid="empty-backline-banner"
              style={{ ...CARD_STYLE, marginTop: 20 }}
            >
              <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 16, color: '#F0EBE0', lineHeight: 1.5 }}>
                This venue hasn&apos;t listed its equipment yet.
              </p>
              <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 13, color: '#a39889', marginTop: 8 }}>
                There is nothing to check a rider against until they do. Nothing is
                missing — it just isn&apos;t known.
              </p>
            </div>
          ) : (
            <>
              {/* Coach-mode exclusions — never silent (§6.3) */}
              {result.excluded_lines?.length > 0 && (
                <div
                  data-testid="excluded-lines-banner"
                  style={{
                    marginTop: 16, padding: '14px 18px', borderRadius: 8,
                    background: 'rgba(200, 146, 62, 0.07)', border: '1px solid rgba(200, 146, 62, 0.3)',
                  }}
                >
                  <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 14, color: '#F0EBE0', fontWeight: 600 }}>
                    {result.excluded_lines.length} line{result.excluded_lines.length === 1 ? '' : 's'} excluded from this check — fix to include {result.excluded_lines.length === 1 ? 'it' : 'them'}
                  </div>
                  <ul style={{ marginTop: 8, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {result.excluded_lines.flatMap((ex) =>
                      ex.messages.map((msg, i) => (
                        <li key={`${ex.line_id}-${i}`} style={{ fontFamily: "'Barlow', sans-serif", fontSize: 13, color: '#d4c5a8' }}>
                          {msg}
                        </li>
                      ))
                    )}
                  </ul>
                  <Link to="/musicians/me/rider" style={{ fontFamily: "'Barlow', sans-serif", fontSize: 13, color: '#C8923E', marginTop: 8, display: 'inline-block' }}>
                    Edit rider →
                  </Link>
                </div>
              )}

              <div className="mt-7 grid gap-8 lg:grid-cols-[7fr_5fr] items-start">
                <div>
                  {RESULTS.map((status) => (
                    <ResultGroup key={status} status={status} lines={byResult[status]} />
                  ))}
                  <BandBringsGroup lines={result.band_provided} />
                </div>
                <div className="lg:sticky lg:top-6">
                  <AboutThisCheck mapVersion={result.map_version} />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
